// Date formatting — the single place the SPA turns an ISO timestamp into
// display text, so every list/detail renders dates identically (TMF-228).
// Reuse these instead of scattering `new Date(x).toLocaleDateString()` calls;
// they share one Intl formatter and handle null/invalid input uniformly.

const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const dateFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Jul 13, 2026, 1:20 PM" — short date + time. Use where the time of day
 *  matters (uploaded-at, completed-at). Empty/invalid input renders "—". */
export function formatDateTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d === null ? "—" : dateTimeFormat.format(d);
}

/** "Jul 13, 2026" — date only, for columns where the clock time is noise. */
export function formatDate(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d === null ? "—" : dateFormat.format(d);
}

const timeFormat = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

const dayFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

/**
 * "5:07 PM" today, "Aug 27" before that — recency for a list a rep scans
 * rather than reads (TMF-405). The year never appears: these are this week's
 * conversations, and "2026" in every row is three characters of noise.
 *
 * `now` is injectable so the today/not-today boundary is testable without
 * freezing the clock.
 */
export function formatRecency(iso: string | null | undefined, now: Date = new Date()): string {
  const d = toDate(iso);
  if (d === null) return "—";
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? timeFormat.format(d) : dayFormat.format(d);
}

/** The UTC calendar day of a timestamp as YYYY-MM-DD, the value a date input
 *  takes, or "" when there is none. UTC because the API stores a day as its
 *  midnight UTC. */
export function calendarDay(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d === null ? "" : d.toISOString().slice(0, 10);
}

function toDate(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === "") return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
